// client/src/components/AdminAffiliates.js
// Vue admin "voir comme" : reproduit exactement l'affichage AffiliatesTransactions
// - Si l'utilisateur sélectionné est une régie -> affiche ses transactions + celles de ses affiliés actifs, avec le sélecteur (Tous / Vous / Affiliés / affilié précis)
// - Si l'utilisateur sélectionné est un affilié indépendant -> affiche uniquement ses transactions
// Multi-devises, commissions récurrentes, KPI par section, logos/pseudos, mêmes règles de filtrage

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import '../CSS/Dashboard.css';
import AdminNavbar from './AdminNavbar';
import { db, storage } from '../../firebase';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  query,
  orderBy,
  where,
  documentId,
  getDocFromCache,
  getDocsFromCache,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { ref, listAll, getDownloadURL } from 'firebase/storage';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
} from 'chart.js';
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTitle, Tooltip, Legend);

// =================== Constantes & Utilitaires ===================

// Devises autorisées & normalisation
const ALLOWED_CURRENCIES = ['EUR', 'USD', 'CAD', 'GBP', 'CHF', 'XOF'];
const normalizeCurrency = (cur) => {
  if (!cur) return 'EUR';
  const s = String(cur).trim().toUpperCase();
  if (s === 'CFA' || s === 'FCFA') return 'XOF';
  return ALLOWED_CURRENCIES.includes(s) ? s : 'EUR';
};

// Commissions récurrentes par devise (alignées)
const COMMISSION_BY_CURRENCY = {
  EUR: 15.75,
  USD: 17.0,
  CAD: 22.0,
  GBP: 14.0,
  CHF: 16.0,
  XOF: 1750,
};

// Formatage montants — XOF sans décimales, sinon 2 décimales (locale fr-FR)
const formatAmount = (amount, currency) => {
  const cur = normalizeCurrency(currency);
  const n = Number(amount || 0);
  const fmt = new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: cur,
    minimumFractionDigits: cur === 'XOF' ? 0 : 2,
    maximumFractionDigits: cur === 'XOF' ? 0 : 2,
  });
  return fmt.format(cur === 'XOF' ? Math.round(n) : n);
};

// Liste ordonnée d’un map {EUR: 10, USD:20} -> [{currency, amount}, ...]
const objectToCurrencyList = (map) => {
  const keys = Object.keys(map || {});
  const ordered = [
    ...ALLOWED_CURRENCIES.filter(c => keys.includes(c)),
    ...keys.filter(k => !ALLOWED_CURRENCIES.includes(k)),
  ];
  return ordered.map(k => ({ currency: k, amount: Number(map[k] || 0) }));
};

// Helpers dates/UID
const extractEntrepriseUid = (timestamp) => (timestamp?.uid || '');
const extractDateTime = (timestamp) => (timestamp?.datetime || '');
const parseUsedTimestamp = (timestampStr) => {
  if (!timestampStr) return new Date();
  if (timestampStr.includes("/")) {
    const parts = timestampStr.split(" ");
    const datePart = parts[0];
    const timePart = parts[1] || '00:00:00';
    const [dayStr, monthStr, yearStr] = datePart.split("/");
    const [hourStr, minuteStr, secondStr] = timePart.split(":");
    return new Date(
      parseInt(yearStr, 10),
      parseInt(monthStr, 10) - 1,
      parseInt(dayStr, 10),
      parseInt(hourStr || '0', 10),
      parseInt(minuteStr || '0', 10),
      parseInt(secondStr || '0', 10)
    );
  }
  return new Date(timestampStr);
};

// Promise dedupe (évite requêtes doublons simultanées)
const inflight = new Map();
const withInflight = async (key, fn) => {
  if (inflight.has(key)) return inflight.get(key);
  const promise = (async () => {
    try { return await fn(); } finally { inflight.delete(key); }
  })();
  inflight.set(key, promise);
  return promise;
};

// ============ Cache (sessionStorage) ============
const getSessionJSON = (key, def = null) => {
  try { const v = sessionStorage.getItem(key); return v ? JSON.parse(v) : def; } catch { return def; }
};
const setSessionJSON = (key, val) => {
  try { sessionStorage.setItem(key, JSON.stringify(val)); } catch { /* noop */ }
};
const delSessionKey = (key) => {
  try { sessionStorage.removeItem(key); } catch { /* noop */ }
};

// TTLs et clés
const PROMOS_CACHE_PREFIX = 'admin_aff_tx_promos_by_uid_v2:'; // + uid  => { ts, data: {promo, transactions}, codesUpdatedAt }
const PROMOS_CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6h

const USERS_CACHE_KEY = 'admin_aff_tx_users_cache_v1'; // map { [uid]: { pseudo, photoURL, ts } }
const USERS_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

const ENT_STATUS_CACHE_KEY = 'admin_aff_tx_ent_status_v1'; // map { [entrepriseUid]: { active: boolean, ts } }
const ENT_STATUS_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

const LOGO_CACHE_PREFIX = 'admin_aff_tx_logo_by_uid_v1:'; // + entrepriseUid => { ts, url }
const LOGO_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

const AFF_LINKED_CACHE_KEY = 'admin_aff_tx_active_affiliates_v1:'; // + regieUid -> { ts, uids[] }
const AFF_LINKED_CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6h

// ============ Caches lecture/écriture ============
const readUsersCacheMap = () => {
  const map = getSessionJSON(USERS_CACHE_KEY, {}) || {};
  const now = Date.now();
  const purged = {};
  for (const k of Object.keys(map)) {
    if (now - (map[k].ts || 0) <= USERS_CACHE_TTL_MS) purged[k] = map[k];
  }
  if (Object.keys(purged).length !== Object.keys(map).length) {
    setSessionJSON(USERS_CACHE_KEY, purged);
  }
  return purged;
};
const writeUsersCacheMap = (mut) => setSessionJSON(USERS_CACHE_KEY, mut);

const readLogoCache = (entUid) => {
  const key = LOGO_CACHE_PREFIX + entUid;
  const cached = getSessionJSON(key);
  if (!cached) return null;
  if (Date.now() - (cached.ts || 0) > LOGO_CACHE_TTL_MS) return null;
  return cached.url || null;
};
const writeLogoCache = (entUid, url) => {
  const key = LOGO_CACHE_PREFIX + entUid;
  setSessionJSON(key, { ts: Date.now(), url: url || null });
};

const readEntStatusCache = () => getSessionJSON(ENT_STATUS_CACHE_KEY, {}) || {};
const writeEntStatusCache = (map) => setSessionJSON(ENT_STATUS_CACHE_KEY, map);

const readAffLinkedCache = (regieUid) => {
  const key = AFF_LINKED_CACHE_KEY + regieUid;
  const cached = getSessionJSON(key);
  if (!cached) return null;
  if (Date.now() - (cached.ts || 0) > AFF_LINKED_CACHE_TTL_MS) return null;
  return cached.uids || null;
};
const writeAffLinkedCache = (regieUid, uids) => {
  const key = AFF_LINKED_CACHE_KEY + regieUid;
  setSessionJSON(key, { ts: Date.now(), uids: Array.isArray(uids) ? uids : [] });
};

const readPromosCache = (uid) => {
  const key = PROMOS_CACHE_PREFIX + uid;
  const cached = getSessionJSON(key);
  if (!cached) return null;
  if (Date.now() - (cached.ts || 0) > PROMOS_CACHE_TTL_MS) return null;
  return cached.data || null;
};
const writePromosCache = (uid, data) => {
  const key = PROMOS_CACHE_PREFIX + uid;
  setSessionJSON(key, { ts: Date.now(), data });
};
const invalidatePromosCache = (uid) => delSessionKey(PROMOS_CACHE_PREFIX + uid);

// =================== Composant ===================

const AdminAffiliates = () => {
  // Sujet sélectionné (utilisateur à "voir comme")
  const [subjects, setSubjects] = useState([]); // {uid, pseudo}
  const [selectedUid, setSelectedUid] = useState('');

  // Rôle / statut du sujet
  const [subjectRole, setSubjectRole] = useState(null);
  const [subjectStatus, setSubjectStatus] = useState(null);
  const isRegie = subjectRole === 'AffiliateRegie';
  const isInactiveSingleAffiliate = !isRegie && subjectStatus && subjectStatus !== 'active';

  // Données calculées (comme AffiliatesTransactions)
  const [promo, setPromo] = useState(null);
  const [transactions, setTransactions] = useState([]);           // self + aff (si régie)
  const [transactionsSelf, setTransactionsSelf] = useState([]);    // self seulement
  const [transactionsAff, setTransactionsAff] = useState([]);      // affiliés (si régie)
  const [loading, setLoading] = useState(true);

  // Métadonnées d’affichage
  const [userPseudos, setUserPseudos] = useState({});
  const [companyLogos, setCompanyLogos] = useState({});

  // KPIs "vue" (adaptés selon sélecteur owner)
  const [viewSumCurrent, setViewSumCurrent] = useState({});
  const [viewSumNext, setViewSumNext] = useState({});
  const [viewClientsCount, setViewClientsCount] = useState(0);

  // Sections : "current", "next", "total"
  const [activeSection, setActiveSection] = useState("current");

  // Filtre propriétaire (uniquement si régie)
  const [ownerFilter, setOwnerFilter] = useState('all'); // 'all' | 'self' | 'all_aff' | <uid>

  // Auth (lecture seule)
  // eslint-disable-next-line
  const [currentUser, setCurrentUser] = useState(null);
  useEffect(() => {
    const auth = getAuth();
    return onAuthStateChanged(auth, (user) => setCurrentUser(user || null));
  }, []);

  // ===== Entreprises actives cache en mémoire + session =====
  const companyStatusRef = useRef(readEntStatusCache());

  const checkCompanyStatus = useCallback(async (entrepriseUid) => {
    if (!entrepriseUid) return false;
    const cached = companyStatusRef.current[entrepriseUid];
    if (typeof cached === 'boolean' && (Date.now() - (cached.ts || 0) <= ENT_STATUS_CACHE_TTL_MS)) {
      return cached.active;
    }
    // Batch unique (mais ici on fait single fallback)
    try {
      const qy = query(collection(db, "entreprises"), where("userId", "==", entrepriseUid));
      const snap = await getDocs(qy);
      let active = false;
      snap.forEach(d => { if ((d.data() || {}).base === 'active') active = true; });
      companyStatusRef.current[entrepriseUid] = { active, ts: Date.now() };
      writeEntStatusCache(companyStatusRef.current);
      return active;
    } catch {
      companyStatusRef.current[entrepriseUid] = { active: false, ts: Date.now() };
      writeEntStatusCache(companyStatusRef.current);
      return false;
    }
  }, []);

  // ===== Active affiliates pour une régie =====
  const getActiveAffiliateUidsForRegie = useCallback(async (regieUid, { forceFresh = false } = {}) => {
    if (!regieUid) return [];
    if (!forceFresh) {
      const cached = readAffLinkedCache(regieUid);
      if (cached) return cached;
    }

    return withInflight(`admin_aff_affuids:${regieUid}:${forceFresh ? 'fresh' : 'cached'}`, async () => {
      const found = new Set();

      // 1) Sous-collec regies/{regieUid}/affiliates
      try {
        const affCol = collection(db, 'regies', regieUid, 'affiliates');
        let snap = null;
        if (!forceFresh) {
          try { snap = await getDocsFromCache(affCol); } catch { snap = null; }
        }
        if (!snap) snap = await getDocs(affCol);
        snap.forEach(d => {
          const id = String(d.id || '').trim();
          const explicit = String((d.data() || {}).affiliateUid || '').trim();
          if (id) found.add(id);
          if (explicit) found.add(explicit);
        });
      } catch {}

      // 2) Fallback users where regieUid == current
      try {
        const qy = query(collection(db, 'users'), where('regieUid', '==', regieUid));
        let snap = null;
        if (!forceFresh) {
          try { snap = await getDocsFromCache(qy); } catch { snap = null; }
        }
        if (!snap) snap = await getDocs(qy);
        snap.forEach(d => {
          const data = d.data() || {};
          const role = String(data.role || '').trim();
          if (['affiliates', 'affiliatepending'].includes(role)) found.add(String(d.id));
        });
      } catch {}

      // 3) Filtrer actifs: Status === 'active' ET role === 'affiliates'
      const arr = Array.from(found);
      const active = new Set();
      const CHUNK = 10;
      for (let i = 0; i < arr.length; i += CHUNK) {
        const slice = arr.slice(i, i + CHUNK);
        try {
          const qUsers = query(collection(db, 'users'), where(documentId(), 'in', slice));
          let snap = null;
          if (!forceFresh) {
            try { snap = await getDocsFromCache(qUsers); } catch { snap = null; }
          }
          if (!snap) snap = await getDocs(qUsers);
          snap.forEach(d => {
            const data = d.data() || {};
            const status = String(data.Status || '').trim().toLowerCase();
            const role = String(data.role || '').trim();
            if (status === 'active' && role === 'affiliates') active.add(d.id);
          });
        } catch {
          for (const u of slice) {
            try {
              const refUser = doc(db, 'users', u);
              let ds = null;
              if (!forceFresh) {
                try { ds = await getDocFromCache(refUser); } catch { ds = null; }
              }
              if (!ds) ds = await getDoc(refUser);
              if (ds.exists()) {
                const data = ds.data() || {};
                const status = String(data.Status || '').trim().toLowerCase();
                const role = String(data.role || '').trim();
                if (status === 'active' && role === 'affiliates') active.add(u);
              }
            } catch {}
          }
        }
      }

      const activeArr = arr.filter(u => active.has(u));
      writeAffLinkedCache(regieUid, activeArr);
      return activeArr;
    });
  }, []);

  // ===== Fetch promos + transactions (avec contrôle de version) — pour un UID donné (self OU affilié) =====
  const fetchPromoCodesForUid = useCallback(async (uid, { forceFresh = false } = {}) => {
    if (!uid) return { promo: null, transactions: [], hasPromo: false, codesUpdatedAt: 0 };
    try {
      // cache
      let cached = null;
      if (!forceFresh) {
        cached = readPromosCache(uid);
        if (cached) {
          // Validation version parent
          try {
            const parentSnap = await getDoc(doc(db, 'code promo', uid));
            const serverUpdatedAt = parentSnap.exists() ? (parentSnap.data()?.codesUpdatedAt?.seconds || 0) : 0;
            const cacheUpdatedAt  = cached.codesUpdatedAt || 0;
            if (serverUpdatedAt <= cacheUpdatedAt) {
              const withOwner = (cached.transactions || []).map(t => ({ ...t, __ownerUid: uid }));
              return { promo: cached.promo || null, transactions: withOwner, hasPromo: cached.hasPromo, codesUpdatedAt: cacheUpdatedAt };
            }
          } catch {
            const withOwner = (cached.transactions || []).map(t => ({ ...t, __ownerUid: uid }));
            return { promo: cached.promo || null, transactions: withOwner, hasPromo: cached.hasPromo, codesUpdatedAt: cached.codesUpdatedAt || 0 };
          }
        }
      }

      // parent
      const userDocRef = doc(db, 'code promo', uid);
      const userDocSnap = await getDoc(userDocRef);
      if (!userDocSnap.exists()) {
        writePromosCache(uid, { promo: null, transactions: [], hasPromo: false, codesUpdatedAt: 0 });
        return { promo: null, transactions: [], hasPromo: false, codesUpdatedAt: 0 };
      }

      const parentData = userDocSnap.data() || {};
      const parentCodesUpdatedAt = parentData?.codesUpdatedAt?.seconds || 0;

      // codes
      const codesCollectionRef = collection(userDocRef, 'codes');
      const qy = query(codesCollectionRef, orderBy("createdAt", "asc"));
      const codesSnapshot = await getDocs(qy);

      const codesArray = [];
      for (const docSnap of codesSnapshot.docs) {
        const data = docSnap.data();
        // Charger la sous-collection activations (nouvelle structure)
        const activSnap = await getDocs(collection(docSnap.ref, 'activations'));
        const newActivations = activSnap.docs.map(a => ({ id: a.id, ...a.data() }));
        // Fusionner avec l'ancien tableau usedTimestamps (rétro-compat)
        const oldTs = Array.isArray(data.usedTimestamps) ? data.usedTimestamps
          : (data.usedTimestamps ? [data.usedTimestamps] : []);
        const mergedTs = [...newActivations, ...oldTs];
        codesArray.push({
          id: docSnap.id,
          ...data,
          usedTimestamps: mergedTs.length > 0 ? mergedTs : undefined,
        });
      }

      const firstPromo = codesArray.length > 0 ? codesArray[0] : parentData;
      const transactionsWithTimestamps = codesArray.filter(code => code.usedTimestamps);

      // Filtrage par entreprises actives
      const filteredTransactions = [];
      for (const trans of transactionsWithTimestamps) {
        const timestamps = Array.isArray(trans.usedTimestamps) ? trans.usedTimestamps : [trans.usedTimestamps];
        let hasActiveTimestamp = false;
        for (const ts of timestamps) {
          const entrepriseUid = ts?.uid || String(ts);
          if (entrepriseUid) {
            const active = await checkCompanyStatus(entrepriseUid);
            if (active) { hasActiveTimestamp = true; break; }
          }
        }
        if (hasActiveTimestamp) filteredTransactions.push({ ...trans, __ownerUid: uid });
      }

      const hasPromo = !!(firstPromo && (firstPromo.promoCode ? String(firstPromo.promoCode).trim() !== '' : true));
      writePromosCache(uid, {
        promo: firstPromo || null,
        transactions: filteredTransactions, // stock sans __ownerUid (ajouté à la lecture du cache)
        hasPromo,
        codesUpdatedAt: parentCodesUpdatedAt || Math.floor(Date.now() / 1000),
      });

      return { promo: firstPromo || null, transactions: filteredTransactions, hasPromo, codesUpdatedAt: parentCodesUpdatedAt || 0 };
    } catch (error) {
      console.error("Erreur get codes promo pour uid", uid, error);
      return { promo: null, transactions: [], hasPromo: false, codesUpdatedAt: 0 };
    }
  }, [checkCompanyStatus]);

  // ===== Liste des sujets (utilisateurs ayant un doc "code promo") =====
  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const promoCollectionRef = collection(db, 'code promo');
        let snap = await getDocs(promoCollectionRef);
        // Tri simple et map
        const list = snap.docs.map(d => ({ uid: d.id, pseudo: d.data()?.pseudo || d.id }));
        list.sort((a,b) => (a.pseudo || '').localeCompare(b.pseudo || ''));
        setSubjects(list);
        if (list[0]) setSelectedUid(list[0].uid);
      } catch (e) {
        console.error("Erreur récupération sujets:", e);
      }
    };
    fetchSubjects();
  }, []);

  // ===== Rôle & chargement transactions pour le sujet sélectionné =====
  useEffect(() => {
    const run = async () => {
      if (!selectedUid) {
        setSubjectRole(null);
        setSubjectStatus(null);
        setPromo(null);
        setTransactions([]);
        setTransactionsSelf([]);
        setTransactionsAff([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setSubjectStatus(null);

      // Récup rôle
      let role = null;
      try {
        const uref = doc(db, 'users', selectedUid);
        const usnap = await getDoc(uref);
        if (usnap.exists()) {
          const data = usnap.data() || {};
          const roleRaw = String(data.role || '').trim();
          const statusRaw = String(data.Status || '').trim();
          role = roleRaw || null;
          setSubjectRole(role);
          setSubjectStatus(statusRaw ? statusRaw.toLowerCase() : null);
        } else {
          role = null;
          setSubjectRole(null);
          setSubjectStatus(null);
        }
      } catch {
        role = null;
        setSubjectRole(null);
        setSubjectStatus(null);
      }

      // Self
      const selfData = await fetchPromoCodesForUid(selectedUid, { forceFresh: false });
      setPromo(selfData.promo || null);
      setTransactionsSelf(selfData.transactions || []);

      if (role !== 'AffiliateRegie') {
        setTransactions(selfData.transactions || []);
        setTransactionsAff([]);
        setLoading(false);
        return;
      }

      // Régie : récupérer affiliés actifs
      const affUids = await getActiveAffiliateUidsForRegie(selectedUid, { forceFresh: true });
      let affAll = [];
      if (affUids.length) {
        const settled = await Promise.allSettled(
          affUids.map(aUid => fetchPromoCodesForUid(aUid, { forceFresh: false }))
        );
        settled.forEach(res => {
          if (res.status === 'fulfilled' && res.value) {
            const arr = res.value.transactions || [];
            if (arr.length) affAll.push(...arr);
          }
        });
      }

      setTransactionsAff(affAll);
      setTransactions([...(selfData.transactions || []), ...affAll]);
      setLoading(false);
    };
    run();
  }, [selectedUid, fetchPromoCodesForUid, getActiveAffiliateUidsForRegie]);

  // ===== Helpers monnaies =====
  const getCurrencyForTrans = (trans, ts) => {
    if (ts?.currency) return normalizeCurrency(ts.currency);
    if (trans?.currency) return normalizeCurrency(trans.currency);
    return 'EUR';
  };

  // ===== Liste filtrée par section (mêmes règles que AffiliatesTransactions) =====
  const getFilteredTransactionsFromList = useCallback((sourceList, section) => {
    const list = Array.isArray(sourceList) ? sourceList : [];
    if (!list.length) return [];

    // min date par entreprise uid (pour displayDate stable)
    const uidMinDateIso = new Map();
    list.forEach(trans => {
      const timestamps = Array.isArray(trans.usedTimestamps) ? trans.usedTimestamps : [trans.usedTimestamps];
      timestamps.forEach(ts => {
        const uidE = ts?.uid || String(ts);
        const dateIso = ts?.datetime || extractDateTime(ts);
        const prev = uidMinDateIso.get(uidE);
        if (!prev || new Date(dateIso) < new Date(prev)) uidMinDateIso.set(uidE, dateIso);
      });
    });

    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear  = now.getFullYear();
    const previousMonth     = currentMonth === 0 ? 11 : currentMonth - 1;
    const previousMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    const nextMonth = (currentMonth + 1) % 12;
    const nextMonthYear = currentMonth === 11 ? currentYear + 1 : currentYear;

    const result = [];

    const addEntry = (trans, isCommission = false, monthYear, forcedCurrency, monthDiff = 0) => {
      const ownerUid = trans.__ownerUid || '';
      if (isCommission) {
        const uidE = monthYear.uid;
        const originalDate = uidMinDateIso.get(uidE);
        const currency = forcedCurrency || 'EUR';
        const _rawTs = Array.isArray(trans.usedTimestamps) ? trans.usedTimestamps[0] : trans.usedTimestamps;
        const _recurringStored = Number(_rawTs?.recurringAmount ?? 0);
        const _grossRaw = Number(_rawTs?.grossAmount ?? 0);
        const _comAmount = _recurringStored > 0
          ? _recurringStored
          : (_grossRaw > 0
            ? (currency === 'XOF' ? Math.round(_grossRaw * 0.35) : Math.round(_grossRaw * 0.35 * 100) / 100)
            : (COMMISSION_BY_CURRENCY[currency] ?? COMMISSION_BY_CURRENCY.EUR));
        const entry = {
          ...trans,
          usedTimestamps: { uid: uidE, datetime: monthYear.datetime, displayDate: originalDate },
          amount: _comAmount,
          __isCommission: true,
          currency,
          __ownerUid: ownerUid,
        };
        result.push({ ...entry, __rowKey: `COM-${ownerUid}-${uidE}-${monthYear.datetime}-${currency}` });
      } else {
        const ts = trans.usedTimestamps || {};
        const uidE = ts?.uid || String(ts);
        const originalDate = uidMinDateIso.get(uidE);
        const recorded = Number(ts.recordedAmount ?? ts.amount ?? 0);
        const gross    = Number(ts.grossAmount ?? 0);
        const fallback = recorded > 0 ? recorded : gross;
        const eff1 = Number(ts.month1Amount ?? 0) > 0 ? Number(ts.month1Amount) : fallback;
        const eff2 = Number(ts.month2Amount ?? 0) > 0 ? Number(ts.month2Amount) : fallback;
        const amount = monthDiff === 0 ? eff1 : eff2;
        const currency = forcedCurrency || getCurrencyForTrans(trans, ts);
        const entry = {
          ...trans,
          usedTimestamps: { ...ts, uid: uidE, datetime: originalDate, displayDate: originalDate },
          amount,
          __isCommission: false,
          currency,
          __ownerUid: ownerUid,
        };
        result.push({ ...entry, __rowKey: `TX-${ownerUid}-${trans.id}-${uidE}-${originalDate}-${currency}` });
      }
    };

    list.forEach(trans => {
      const timestamps = Array.isArray(trans.usedTimestamps) ? trans.usedTimestamps : [trans.usedTimestamps];
      timestamps.forEach(ts => {
        if (!ts || !ts.datetime) return;
        const dateIso = ts.datetime || extractDateTime(ts);
        const transDate = parseUsedTimestamp(dateIso);
        const diffToCurrent  = (currentYear - transDate.getFullYear()) * 12 + (currentMonth  - transDate.getMonth());
        const diffToPrevious = (previousMonthYear - transDate.getFullYear()) * 12 + (previousMonth - transDate.getMonth());
        const diffToNext     = (nextMonthYear - transDate.getFullYear()) * 12 + (nextMonth - transDate.getMonth());

        const commFor = (year, month) => ({
          uid: ts?.uid || String(ts),
          datetime: new Date(year, month, 1).toISOString()
        });

        const currency = getCurrencyForTrans(trans, ts);

        if (section === 'current') {
          if (transDate.getFullYear() === currentYear && transDate.getMonth() === currentMonth) {
            addEntry({ ...trans, usedTimestamps: ts }, false, null, currency, diffToCurrent); // mois 1 (diff=0)
          }
          if (transDate.getFullYear() === previousMonthYear && transDate.getMonth() === previousMonth) {
            addEntry({ ...trans, usedTimestamps: ts }, false, null, currency, diffToCurrent); // mois 2 (diff=1)
          }
          if (diffToPrevious >= 2) addEntry(trans, true, commFor(previousMonthYear, previousMonth), currency);
          if (diffToCurrent  >= 2) addEntry(trans, true, commFor(currentYear, currentMonth), currency);

        } else if (section === 'next') {
          if (transDate.getFullYear() === currentYear && transDate.getMonth() === currentMonth) {
            addEntry({ ...trans, usedTimestamps: ts }, false, null, currency, diffToNext); // mois 2 le mois prochain (diff=1)
          }
          if (diffToNext >= 2) addEntry(trans, true, commFor(nextMonthYear, nextMonth), currency);

        } else { // total
          addEntry({ ...trans, usedTimestamps: ts }, false, null, currency, 0);
        }
      });
    });

    return result;
  }, []);

  const sumListByCurrency = (list) => {
    const map = {};
    list.forEach(t => {
      const cur = normalizeCurrency(t.currency);
      map[cur] = (map[cur] || 0) + Number(t.amount || 0);
    });
    return map;
  };

  // ======= Source “vue” selon sélecteur (pour les 3 KPI du haut) =======
  const ownerScopedRawList = useMemo(() => {
    if (!isRegie) return transactionsSelf;

    if (ownerFilter === 'all') return [...transactionsSelf, ...transactionsAff];
    if (ownerFilter === 'self') return [...transactionsSelf];
    if (ownerFilter === 'all_aff') return [...transactionsAff];
    // affilié précis
    return transactionsAff.filter(t => (t.__ownerUid || '') === ownerFilter);
  }, [isRegie, ownerFilter, transactionsSelf, transactionsAff]);

  // ======= KPI “vue” =======
  useEffect(() => {
    const curList = getFilteredTransactionsFromList(ownerScopedRawList, 'current');
    const nxtList = getFilteredTransactionsFromList(ownerScopedRawList, 'next');
    setViewSumCurrent(sumListByCurrency(curList));
    setViewSumNext(sumListByCurrency(nxtList));

    // Clients uniques du mois courant (sur la source filtrée)
    const setUids = new Set();
    const now = new Date();
    const m = now.getMonth();
    const y = now.getFullYear();
    (ownerScopedRawList || []).forEach(trans => {
      const timestamps = Array.isArray(trans.usedTimestamps) ? trans.usedTimestamps : [trans.usedTimestamps];
      timestamps.forEach(ts => {
        if (!ts?.datetime) return;
        const d = parseUsedTimestamp(ts.datetime);
        const uidE = extractEntrepriseUid(ts);
        if (d.getFullYear() === y && d.getMonth() === m) setUids.add(uidE);
      });
    });
    setViewClientsCount(setUids.size);
  }, [ownerScopedRawList, getFilteredTransactionsFromList]);

  // ====== Options du sélecteur propriétaire (régie) ======
  const ownerOptions = useMemo(() => {
    if (!isRegie) {
      return [{ value: 'self', label: 'Vous' }];
    }
    const owners = new Set();
    (transactionsSelf || []).forEach(t => owners.add(t.__ownerUid || selectedUid));
    (transactionsAff || []).forEach(t => owners.add(t.__ownerUid));

    const items = [];
    items.push({ value: 'all', label: 'Total' });
    items.push({ value: 'self', label: 'Régie' });
    items.push({ value: 'all_aff', label: 'Tous les affiliates' });
    Array.from(owners)
      .filter(o => o && o !== selectedUid)
      .sort()
      .forEach(o => {
        const name = userPseudos[o] || o;
        items.push({ value: o, label: name });
      });
    return items;
  }, [isRegie, transactionsSelf, transactionsAff, userPseudos, selectedUid]);

  // ===== Pseudos batch (entreprises + owners) =====
  const fetchUserPseudosBatch = async (uids) => {
    const unique = [...new Set((uids || []).filter(Boolean))];
    if (!unique.length) return {};
    const cache = readUsersCacheMap();
    const need = unique.filter(u => !(cache[u] && cache[u].pseudo));
    const CHUNK = 10;

    for (let i = 0; i < need.length; i += CHUNK) {
      const slice = need.slice(i, i + CHUNK);
      try {
        const qUsers = query(collection(db, 'users'), where(documentId(), 'in', slice));
        const snap = await getDocs(qUsers);
        snap.forEach(d => {
          const data = d.data() || {};
          cache[d.id] = {
            pseudo: data.pseudo || data.displayName || d.id,
            photoURL: data.photoURL || (cache[d.id]?.photoURL ?? null),
            ts: Date.now(),
          };
        });
        slice.forEach(u => { if (!cache[u]) cache[u] = { pseudo: u, photoURL: null, ts: Date.now() }; });
      } catch {
        for (const u of slice) {
          try {
            const ds = await getDoc(doc(db, 'users', u));
            if (ds.exists()) {
              const data = ds.data() || {};
              cache[u] = { pseudo: data.pseudo || data.displayName || u, photoURL: data.photoURL || null, ts: Date.now() };
            } else {
              cache[u] = { pseudo: u, photoURL: null, ts: Date.now() };
            }
          } catch {
            cache[u] = { pseudo: u, photoURL: null, ts: Date.now() };
          }
        }
      }
    }
    writeUsersCacheMap(cache);

    const out = {};
    unique.forEach(u => { out[u] = cache[u]?.pseudo || u; });
    return out;
  };

  // — Entreprise pseudos + Owner pseudos pour légendes/colonnes
  useEffect(() => {
    const run = async () => {
      const tableSource = isRegie ? transactions : transactionsSelf;
      const sectionList = getFilteredTransactionsFromList(tableSource, activeSection);

      const entUids = [...new Set(sectionList.map(t => extractEntrepriseUid(t.usedTimestamps)).filter(Boolean))];
      const ownerUids = [...new Set(sectionList.map(t => (t.__ownerUid || selectedUid)).filter(Boolean))];
      const batch = await fetchUserPseudosBatch([...entUids, ...ownerUids, selectedUid]);
      setUserPseudos(prev => ({ ...prev, ...batch }));
    };
    if ((isRegie ? transactions : transactionsSelf).length > 0) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactions, transactionsSelf, activeSection, isRegie, getFilteredTransactionsFromList, selectedUid]);

  // ===== Logos =====
  const fetchCompanyLogo = async (entUid) => {
    if (!entUid) return null;
    const cachedUrl = readLogoCache(entUid);
    if (cachedUrl !== null) return cachedUrl;

    try {
      const ds = await getDoc(doc(db, 'users', entUid));
      if (ds.exists()) {
        const data = ds.data() || {};
        if (data.photoURL) {
          writeLogoCache(entUid, data.photoURL);
          return data.photoURL;
        }
      }
    } catch {}

    try {
      const logoFolderRef = ref(storage, `${entUid}/photo-profil`);
      const res = await listAll(logoFolderRef);
      if (res.items.length === 0) { writeLogoCache(entUid, null); return null; }
      const sortedItems = res.items.sort((a, b) => {
        const tsA = parseInt(a.name.split('_')[0], 10);
        const tsB = parseInt(b.name.split('_')[0], 10);
        return (isNaN(tsB) ? 0 : tsB) - (isNaN(tsA) ? 0 : tsA);
      });
      const latestItem = sortedItems[0];
      const url = await getDownloadURL(latestItem);
      writeLogoCache(entUid, url);
      return url;
    } catch (error) {
      console.error("Erreur logo uid:", entUid, error);
      writeLogoCache(entUid, null);
      return null;
    }
  };

  useEffect(() => {
    const run = async () => {
      const tableSource = isRegie ? transactions : transactionsSelf;
      const sectionList = getFilteredTransactionsFromList(tableSource, activeSection);
      const entUids = [...new Set(sectionList.map(t => extractEntrepriseUid(t.usedTimestamps)).filter(Boolean))];
      const logos = {};
      await Promise.all(entUids.map(async (e) => {
        const url = await fetchCompanyLogo(e);
        logos[e] = url;
      }));
      setCompanyLogos(prev => ({ ...prev, ...logos }));
    };
    if ((isRegie ? transactions : transactionsSelf).length > 0) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactions, transactionsSelf, activeSection, isRegie, getFilteredTransactionsFromList]);

  // ===== Génération code promo (admin) pour le sujet sélectionné =====
  const handleGenerate = async () => {
    if (!selectedUid) return;
    try {
      setLoading(true);
      const usersCache = readUsersCacheMap();
      let pseudo = usersCache[selectedUid]?.pseudo || selectedUid;
      if (pseudo === selectedUid) {
        try {
          const userDocRef  = doc(db, 'users', selectedUid);
          const userDocSnap = await getDoc(userDocRef);
          pseudo  = userDocSnap.exists() ? (userDocSnap.data().pseudo || selectedUid) : selectedUid;
          usersCache[selectedUid] = { ...(usersCache[selectedUid] || {}), pseudo, ts: Date.now() };
          writeUsersCacheMap(usersCache);
        } catch { /* noop */ }
      }

      const rootRef  = doc(db, 'code promo', selectedUid);
      await setDoc(rootRef, { uid: selectedUid, pseudo }, { merge: true });

      invalidatePromosCache(selectedUid);
      // Forcer un refresh complet du sujet en re-déclenchant le useEffect via setSelectedUid
      const keep = selectedUid;
      setSelectedUid('');
      setTimeout(() => setSelectedUid(keep), 0);
    } catch (error) {
      console.error("Erreur lors de la création du code promo (admin) :", error);
    } finally {
      setLoading(false);
    }
  };

  // ====== Taille du chip selon longueur pseudo
  const getChipSize = (pseudo = '') => {
    const len = String(pseudo).length;
    if (len <= 10) return 'lg';
    if (len <= 18) return 'md';
    return 'sm';
  };

  // ===== Liste filtrée utilisée pour le tableau (régie = toutes transactions, sinon directes) =====
  const baseTableTransactions = getFilteredTransactionsFromList(
    isRegie ? transactions : transactionsSelf,
    activeSection
  );

  // Appliquer filtre "propriétaire" au tableau (régie)
  const tableTransactions = useMemo(() => {
    if (!isRegie) return baseTableTransactions;
    if (ownerFilter === 'all') return baseTableTransactions;
    if (ownerFilter === 'self') return baseTableTransactions.filter(t => (t.__ownerUid || selectedUid) === selectedUid);
    if (ownerFilter === 'all_aff') return baseTableTransactions.filter(t => (t.__ownerUid || '') !== selectedUid);
    return baseTableTransactions.filter(t => (t.__ownerUid || '') === ownerFilter);
  }, [baseTableTransactions, ownerFilter, isRegie, selectedUid]);

  // ———————— UI styles (reprise de AffiliatesTransactions) ————————
  const EcobillUI = () => (
    <style>{`
  .aff-wrap { width: 100%; display: flex; flex-direction: column; gap: 20px; }
  .dashboard-container .aff-h1 { margin: 0; text-align: left; }

  .aff-topbar { display:flex; align-items:flex-end; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin: 0; }
  .aff-sub { color: var(--eb-muted); }

  .aff-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr)); gap: 16px; margin: 0; }
  .aff-card { background: var(--eb-surface); border:1px solid var(--eb-border); border-radius: var(--eb-radius-lg); padding: 20px 22px; box-shadow: var(--eb-shadow-sm); text-align:left; min-width: 0; }
  .dashboard-container .aff-card h3 { margin: 0; color: var(--eb-muted); font-size: 13.5px; font-weight: 600; text-align:left; letter-spacing: 0; }
  .aff-card .val { font-size: 28px; font-weight: 700; letter-spacing: -0.02em; color: var(--eb-text); margin-top: 6px; text-align:left; line-height: 1.15; }
  .aff-card .val small { font-size: 13.5px; font-weight: 500; color: var(--eb-muted); margin-left: 6px; letter-spacing: 0; }

  .aff-pill-wrap { display:flex; flex-wrap:wrap; gap:6px; justify-content:flex-start; margin-top:8px; align-items: center; }
  .aff-pill { display:inline-block; padding:4px 10px; border-radius: var(--eb-radius-pill); background: var(--eb-surface-2); border:1px solid var(--eb-border); font-weight:700; font-size: 15px; color: var(--eb-text); }
  .aff-plus { font-weight: 700; color: var(--eb-subtle); }
  .aff-danger { color: var(--eb-danger) !important; }
  .aff-pill.aff-danger { border-color: #fbd0cb; background: var(--eb-danger-bg); }

  .aff-section-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
  .dashboard-container .aff-h2 { margin: 0; font-size: 18px; font-weight: 650; color: var(--eb-text); text-align: left; }

  .aff-tabs { display: inline-flex; gap: 4px; padding: 4px; flex-wrap: wrap; background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius); box-shadow: var(--eb-shadow-xs); }
  .aff-tab { cursor: pointer; min-height: 36px; padding: 0 14px; border-radius: var(--eb-radius-sm); background: transparent; color: var(--eb-text-2); border: 0; font-weight: 600; font-size: 14px; transition: background-color .15s ease, color .15s ease; }
  .aff-tab:hover { background: var(--eb-surface-2); color: var(--eb-text); }
  .aff-tab.is-active { background: var(--eb-brand); color: #fff; }

  .aff-controls { display:flex; justify-content:flex-end; align-items:center; gap:10px; flex-wrap: wrap; }
  .aff-label { font-size: 13.5px; font-weight: 600; color: var(--eb-text-2); }
  .aff-select { min-height: 42px; min-width: 180px; max-width: 100%; border:1px solid var(--eb-border-strong); border-radius: var(--eb-radius-sm); padding: 0 12px; background: var(--eb-surface); color: var(--eb-text); font-weight: 500; font-size: 14px; }

  .aff-table-wrap { overflow: auto; border:1px solid var(--eb-border); border-radius: var(--eb-radius-lg); background: var(--eb-surface); box-shadow: var(--eb-shadow-sm); }
  table.aff-table { width: 100%; border-collapse: separate; border-spacing: 0; min-width: 760px; }
  .aff-table thead th { position: sticky; top: 0; background: var(--eb-surface-2); color: var(--eb-muted); text-align: left; font-size: 12.5px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; padding: 12px 16px; border-bottom: 1px solid var(--eb-border); }
  .aff-table thead th.aff-th-right { text-align: right; }
  .aff-table tbody td { padding: 14px 16px; border-bottom: 1px solid var(--eb-border); vertical-align: middle; color: var(--eb-text-2); font-size: 14px; }
  .aff-table tbody tr:last-child td { border-bottom: 0; }
  .aff-table tbody tr:hover { background: var(--eb-brand-50); }
  .aff-amt { text-align: right; font-weight: 700; color: var(--eb-text) !important; font-variant-numeric: tabular-nums; }
  .aff-amt.aff-amt-inactive { color: var(--eb-danger) !important; }

  .aff-company { display:flex; align-items:center; gap: 12px; }
  .aff-logo { width: 40px; height: 40px; border-radius: var(--eb-radius-sm); background: var(--eb-surface-2); border: 1px solid var(--eb-border); object-fit: cover; flex-shrink: 0; }
  .aff-company-name { font-weight: 600; color: var(--eb-text); }
  .aff-company-date { display:block; font-size: 12.5px; color: var(--eb-muted); margin-top:2px; }
  .aff-code-cell { display:flex; flex-direction:column; gap:6px; max-width:100%; }
  .aff-code-meta { font-size: 12.5px; color: var(--eb-muted); }

  .aff-chip { display:inline-flex; align-items:center; gap:6px; font-weight:600; padding:4px 10px; border-radius: var(--eb-radius-pill); border:1px solid var(--eb-border); background: var(--eb-surface-2); color: var(--eb-text); max-width:100%; width: fit-content; }
  .aff-chip.lg { font-size: 13px; }
  .aff-chip.md { font-size: 12.5px; }
  .aff-chip.sm { font-size: 12px; }
  .aff-chip.comm { background: var(--eb-brand-50); border-color: var(--eb-brand-100); color: var(--eb-brand); }
  .chip-owner { font-weight: 700; color: var(--eb-brand); max-width: 180px; white-space: nowrap; overflow:hidden; text-overflow: ellipsis; }
  @media(min-width:1024px){ .chip-owner { max-width: 260px; } }

  .aff-empty { text-align:center; color: var(--eb-muted); padding: 32px 16px; font-size: 14px; background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); box-shadow: var(--eb-shadow-sm); }
  .aff-cta { align-self: flex-start; }

  .aff-skeleton { background: linear-gradient(90deg, var(--eb-border) 25%, var(--eb-surface-2) 37%, var(--eb-border) 63%); border-radius: var(--eb-radius); background-size: 400% 100%; animation: shimmer 1.4s ease-in-out infinite; height: 56px; margin: 8px 0; }
  @keyframes shimmer { 0% { background-position: 100% 0 } 100% { background-position: 0 0 } }

  @media(max-width: 640px){
    table.aff-table { min-width: 0; border-spacing: 0; }
    .aff-table thead { display: none; }
    .aff-table tbody tr { display: flex; flex-direction: column; gap: 6px; padding: 12px 14px; border-bottom: 1px solid var(--eb-border); }
    .aff-table tbody tr:last-child { border-bottom: 0; }
    .aff-table tbody td { display: grid; grid-template-columns: 96px 1fr; align-items: center; padding: 4px 0; border: 0; }
    .aff-table tbody td::before { content: attr(data-col); font-size: 12px; color: var(--eb-muted); font-weight: 600; text-transform: uppercase; letter-spacing: .04em; }
    .aff-table tbody td[data-col="Entreprise"] { order: 1; }
    .aff-table tbody td[data-col="Montant"]    { order: 2; }
    .aff-table tbody td[data-col="Code"]       { order: 3; }
    .aff-amt { text-align: left; }
    .aff-topbar { flex-direction: column; align-items: stretch; }
    .aff-controls { justify-content: flex-start; width: 100%; }
    .aff-select { flex: 1 1 100%; }
    .aff-tabs { display: flex; }
    .aff-tab { flex: 1; padding: 0 6px; font-size: 13.5px; white-space: nowrap; }
    .aff-section-head { flex-direction: column; align-items: stretch; }
    .aff-cta { width: 100%; white-space: normal; text-align: left; padding: 12px 16px; line-height: 1.3; }
  }
`}</style>
  );

  // KPI rendu montants
  const KpiAmount = ({ sumMap, highlight = false }) => {
    const list = objectToCurrencyList(sumMap);
    const displayValue = list.length ? formatAmount(list[0].amount, list[0].currency) : formatAmount(0, 'EUR');

    if (list.length <= 1) {
      return (
        <div
          className={highlight ? 'val aff-danger' : 'val'}
        >
          {displayValue}
        </div>
      );
    }


    return (
      <div className="aff-pill-wrap">
        {list.map(({ currency, amount }, i) => (
          <React.Fragment key={currency}>
            {i > 0 && <span className={highlight ? 'aff-plus aff-danger' : 'aff-plus'}>+</span>}
            <span className={highlight ? 'aff-pill aff-danger' : 'aff-pill'}>{formatAmount(amount, currency)}</span>
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div className="dashboard-container">
      <AdminNavbar />
      <div className="aff-wrap">
        <EcobillUI />

        {/* Topbar : sélection sujet + (si régie) sélecteur owner */}
        <div className="aff-topbar">
          <div>
            <h1 className="aff-h1 eb-page-title">Transactions (Admin)</h1>
            <p className="eb-page-subtitle">Revenus et commissions par affilié</p>
          </div>

          <div className="aff-controls">
            <label htmlFor="subjectSelect" className="aff-label">Voir en tant que :</label>
            <select
              id="subjectSelect"
              className="aff-select"
              value={selectedUid}
              onChange={(e) => {
                setSelectedUid(e.target.value);
                setOwnerFilter('all');
              }}
            >
              {subjects.map(s => (
                <option key={s.uid} value={s.uid}>{s.pseudo}</option>
              ))}
            </select>

            {isRegie ? (
              <>
                <label htmlFor="ownerFilter" className="aff-label">Afficher :</label>
                <select
                  id="ownerFilter"
                  className="aff-select"
                  value={ownerFilter}
                  onChange={(e) => setOwnerFilter(e.target.value)}
                >
                  {ownerOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </>
            ) : null}
          </div>
        </div>

        {/* 3 KPI alignés */}
        <div className="aff-cards" aria-label="Résumé (selon le filtre sélectionné)">
          <section className="aff-card" aria-labelledby="card-now">
            <h3 id="card-now">
              {new Date().toLocaleString('fr-FR', { month: 'long' }).replace(/^\w/, c => c.toUpperCase())}
            </h3>
            <KpiAmount sumMap={viewSumCurrent} highlight={isInactiveSingleAffiliate} />
          </section>

          <section className="aff-card" aria-labelledby="card-next">
            <h3 id="card-next">
              {(() => {
                const nextMonth = new Date(new Date().setMonth(new Date().getMonth() + 1))
                  .toLocaleString('fr-FR', { month: 'long' })
                  .replace(/^\w/, c => c.toUpperCase());
                return nextMonth;
              })()}
            </h3>
            <KpiAmount sumMap={viewSumNext} highlight={isInactiveSingleAffiliate} />
          </section>

          <section className="aff-card" aria-labelledby="card-clients">
            <h3 id="card-clients">
              {new Date().toLocaleString('fr-FR', { month: 'long' }).replace(/^\w/, c => c.toUpperCase())} — Clients
            </h3>
            <div className="val">
              {viewClientsCount}
              <small>
                Nouveaux clients
              </small>
            </div>
          </section>
        </div>

        <div className="aff-section-head" style={{ marginTop: 12 }}>
        <h2 className="aff-h2">
          {isRegie ? 'Transactions' : 'Transactions (affilié)'}
        </h2>

        {/* Tabs */}
        <div className="aff-tabs" role="tablist" aria-label="Sections de transactions">
          <button
            role="tab"
            aria-selected={activeSection === 'current'}
            className={`aff-tab ${activeSection === 'current' ? 'is-active' : ''}`}
            onClick={() => setActiveSection('current')}
          >Mois en cours</button>
          <button
            role="tab"
            aria-selected={activeSection === 'next'}
            className={`aff-tab ${activeSection === 'next' ? 'is-active' : ''}`}
            onClick={() => setActiveSection('next')}
          >Mois à venir</button>
          <button
            role="tab"
            aria-selected={activeSection === 'total'}
            className={`aff-tab ${activeSection === 'total' ? 'is-active' : ''}`}
            onClick={() => setActiveSection('total')}
          >Total</button>
        </div>
        </div>

        {/* Content */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {loading ? (
            <>
              <div className="aff-skeleton" />
              <div className="aff-skeleton" />
              <div className="aff-skeleton" />
            </>
          ) : (
            promo ? (
              tableTransactions.length > 0 ? (
                <div className="aff-table-wrap" role="region" aria-label="Tableau des transactions">
                  <table className="aff-table">
                    <thead>
                      <tr>
                        <th>Entreprise</th>
                        <th>Code</th>
                        <th className="aff-th-right">Montant</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableTransactions.map((trans, index) => {
                        const ts = trans.usedTimestamps;
                        const rawDate = ts.displayDate || extractDateTime(ts);
                        const dateObj = parseUsedTimestamp(rawDate);
                        const formattedDate = dateObj.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
                        const entrepriseUid = extractEntrepriseUid(ts);
                        const isCommission = !!trans.__isCommission;
                        const amountDisplay = formatAmount(trans.amount, trans.currency);
                        const amountClasses = ['aff-amt'];
                        if (isInactiveSingleAffiliate) amountClasses.push('aff-amt-inactive');

                        // Pseudo du propriétaire du code (régie/affilié)
                        const ownerUid = trans.__ownerUid || selectedUid;
                        const ownerPseudo = userPseudos[ownerUid] || ownerUid;
                        const chipSize = getChipSize(ownerPseudo);

                        const fallbackKey = `${trans.id || 'tx'}-${entrepriseUid || 'noent'}-${rawDate || 'nodate'}-${index}`;
                        return (
                          <tr key={`${trans.__rowKey || fallbackKey}-${index}`}>
                            <td data-col="Entreprise">
                              <div className="aff-company">
                                {companyLogos[entrepriseUid] ? (
                                  <img src={companyLogos[entrepriseUid]} alt="logo" className="aff-logo" />
                                ) : (
                                  <div className="aff-logo" />
                                )}
                                <div>
                                  <span className="aff-company-name">{userPseudos[entrepriseUid] || entrepriseUid}</span>
                                  <small className="aff-company-date">{formattedDate}</small>
                                </div>
                              </div>
                            </td>
                            <td data-col="Code">
                              <div className="aff-code-cell">
                                <span className={`aff-chip ${isCommission ? 'comm' : ''} ${chipSize}`}>
                                  {isCommission ? 'Commission récurrente' : (trans.promoCode || trans.label || 'Code')}
                                  <span className="chip-owner"> · {ownerPseudo}</span>
                                </span>
                                {!isCommission && (
                                  <div className="aff-code-meta">
                                    {trans.percentage ?? ts?.percentage ?? 0}% pendant {trans.MonthNumber ?? ts?.MonthNumber ?? ts?.months ?? 0} mois
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className={amountClasses.join(' ')} data-col="Montant">{amountDisplay}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="aff-empty">Aucune transaction pour cette section.</div>
              )
            ) : (
              <button className="aff-cta eb-btn eb-btn--primary" onClick={handleGenerate}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                  <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Réclamer/initialiser le code promo pour cet utilisateur
              </button>
            )
          )}
          
        </div>
      </div>

    </div>
  );
};

export default AdminAffiliates
